import { useState, useEffect } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { AREA_CODES, areaColor } from "../constants";
import { useCurrency } from "../../../currency/CurrencyProvider";
import { Price } from "../../models/price";
import { useLanguage } from "../../../i18n/LanguageProvider";
import "./currentPrice.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Plugin to draw a vertical line at the current (now) index
const nowLinePlugin = {
  id: "nowLine",
  afterDatasetsDraw(chart: any, _args: any, pluginOptions: any) {
    const index = pluginOptions?.index;
    const color = pluginOptions?.color || "rgba(0,0,0,0.8)";
    if (index === undefined || index === null || index < 0) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || !meta.data[index]) return;

    const x = meta.data[index].x;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 2;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(nowLinePlugin);

// Render price timestamps in Swedish wall-clock time regardless of the viewer's
// browser timezone — Swedish spot prices are inherently in Europe/Stockholm time.
const SWEDISH_TZ = "Europe/Stockholm";

interface CurrentPriceProps {
  prices: Record<string, Price[]>;
  loadingPrices: boolean;
  commodity?: 'electricity' | 'copper' | 'crude' | 'tesla' | 'nvidia' | 'shell' | 'nextera' | 'enphase';
  /** 'ALL' (or undefined) shows every SE area; a specific zone (e.g. 'SE3')
   *  narrows the historical view to just that area. */
  selectedArea?: string;
  onExpand?: () => void;
}

const CurrentPrice = ({ prices, loadingPrices, commodity = 'electricity', selectedArea = 'ALL', onExpand }: CurrentPriceProps) => {
  const { t, locale } = useLanguage();
  const { unit } = useCurrency();
  const [viewMode, setViewMode] = useState<'today' | 'week' | 'month'>('today');

  // Track the active theme so asset colors stay visible in dark mode (crude's
  // near-black would otherwise vanish against the dark background).
  const [darkMode, setDarkMode] = useState(() => document.documentElement.getAttribute('data-bs-theme') === 'dark');
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDarkMode(document.documentElement.getAttribute('data-bs-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
    return () => observer.disconnect();
  }, []);

  // Filter prices based on view mode
  const getFilteredPrices = (allPrices: Record<string, Price[]>): Record<string, Price[]> => {
    const now = new Date();
    const filteredPrices: Record<string, Price[]> = {};
    
    Object.keys(allPrices).forEach(area => {
      if (viewMode === 'today') {
        // Electricity has day-ahead spot prices for tomorrow (published ~13:00
        // CET), so its "today" tab shows today + tomorrow. Commodities/stocks
        // have no day-ahead, so they show today only.
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const daysSpan = commodity === 'electricity' ? 2 : 1;
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysSpan);
        filteredPrices[area] = allPrices[area].filter(price => {
          const priceTime = new Date(price.timestamp);
          return priceTime >= todayStart && priceTime < todayEnd;
        });
      } else if (viewMode === 'week') {
        // Week: show last 7 days
        const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        filteredPrices[area] = allPrices[area].filter(price => {
          const priceTime = new Date(price.timestamp);
          return priceTime >= weekStart;
        });
      } else {
        // Month: show last 30 days
        const monthStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        filteredPrices[area] = allPrices[area].filter(price => {
          const priceTime = new Date(price.timestamp);
          return priceTime >= monthStart;
        });
      }
    });
    
    return filteredPrices;
  };

  const filteredPrices = getFilteredPrices(prices);

  // Get commodity color from selector. Copper/crude/electricity get lighter
  // variants in dark mode (matching the main forecast chart) so the line and the
  // summary's left border stay visible against the dark surface.
  const getCommodityColor = (commodityType: 'electricity' | 'copper' | 'crude' | 'tesla' | 'nvidia' | 'shell' | 'nextera' | 'enphase'): string => {
    switch (commodityType) {
      case 'copper':
        return darkMode ? 'rgba(212, 148, 85, 1)' : 'rgba(184, 115, 51, 1)'; // #b87333
      case 'crude':
        return darkMode ? 'rgba(148, 163, 184, 1)' : 'rgba(17, 24, 39, 1)'; // slate-gray in dark, #111827 in light
      case 'tesla':
        return 'rgba(232, 33, 39, 1)'; // #e82127
      case 'nvidia':
        return 'rgba(118, 185, 0, 1)'; // #76b900
      case 'shell':
        return 'rgba(251, 206, 7, 1)'; // #fbce07
      case 'nextera':
        return 'rgba(0, 119, 200, 1)'; // #0077c8
      case 'enphase':
        return 'rgba(243, 112, 33, 1)'; // #f37021
      case 'electricity':
      default:
        return darkMode ? 'rgba(96, 165, 250, 1)' : 'rgba(13, 110, 253, 1)'; // #0d6efd
    }
  };

  // Per-area electricity palette, dark-aware (matches the forecast chart).

  const darkenRgba = (rgba: string, factor: number = 0.8): string => {
    const match = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i);
    if (!match) return rgba;
    const r = Math.max(0, Math.min(255, Math.round(parseInt(match[1], 10) * factor)));
    const g = Math.max(0, Math.min(255, Math.round(parseInt(match[2], 10) * factor)));
    const b = Math.max(0, Math.min(255, Math.round(parseInt(match[3], 10) * factor)));
    const a = match[4] !== undefined ? parseFloat(match[4]) : 1;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  // Get current hour price for each area
  const getCurrentPrice = (areaPrices: Price[]): Price | null => {
    if (!areaPrices || areaPrices.length === 0) return null;
    
    const now = new Date();
    const currentHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours());
    
    // Find the price for the current hour
    const currentHourPrice = areaPrices.find(price => {
      const priceTime = new Date(price.timestamp);
      return priceTime.getTime() === currentHour.getTime();
    });
    
    // If found, return it
    if (currentHourPrice) return currentHourPrice;
    
    // For stocks and commodities, fall back to the most recent price if no current hour price exists
    // (markets may be closed on weekends/evenings)
    const isStockOrCommodity = commodity === 'copper' || commodity === 'crude' || 
                               commodity === 'tesla' || commodity === 'nvidia' || 
                               commodity === 'shell' || commodity === 'nextera' || 
                               commodity === 'enphase';
    
    if (isStockOrCommodity) {
      // Sort by timestamp descending and return the most recent
      const sortedPrices = [...areaPrices].sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      return sortedPrices[0] || null;
    }
    
    // For electricity, return null if no current hour price
    return null;
  };

  // Localized display name for the asset. Commodities have i18n keys; stock
  // tickers are proper nouns and stay as-is.
  const getDisplayName = (): string => {
    switch (commodity) {
      case 'copper': return t('commodity.copper');
      case 'crude': return t('commodity.crude');
      case 'tesla': return 'Tesla';
      case 'nvidia': return 'Nvidia';
      case 'shell': return 'Shell';
      case 'nextera': return 'Nextera';
      case 'enphase': return 'Enphase';
      default: return commodity || '';
    }
  };

  // Create chart data for an area
  const createAreaChartData = (areaPrices: Price[], area: string, isCommodity: boolean = false) => {
    if (!areaPrices || areaPrices.length === 0) return null;

    const sortedPrices = areaPrices.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const currentPrice = getCurrentPrice(areaPrices);

    // Format labels based on view mode
    const labels = sortedPrices.map(price => {
      const date = new Date(price.timestamp);
      if (viewMode === 'month') {
        return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: SWEDISH_TZ });
      } else if (viewMode === 'week') {
        return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', hour: '2-digit', timeZone: SWEDISH_TZ });
      } else {
        // today + tomorrow spans 48h, so include the weekday to disambiguate the
        // two days' otherwise-identical hour labels.
        return date.toLocaleString(locale, { weekday: 'short', hour: '2-digit', minute: '2-digit', timeZone: SWEDISH_TZ });
      }
    });
    const data = sortedPrices.map(price => price.value);

    // Find the index of the current price for highlighting
    const currentPriceIndex = currentPrice ? sortedPrices.findIndex(price => price.timestamp === currentPrice.timestamp) : -1;

    // Only the "now" point gets a dot — keeps the line uncluttered.
    const baseColor = isCommodity ? getCommodityColor(commodity!) : areaColor(area, darkMode);
    const highlightColor = darkenRgba(baseColor, 0.7);
    const pointBackgroundColors = data.map((_, index) =>
      index === currentPriceIndex ? highlightColor : 'rgba(0,0,0,0)'
    );
    const pointBorderColors = data.map((_, index) =>
      index === currentPriceIndex ? '#ffffff' : 'rgba(0,0,0,0)'
    );
    const pointRadius = data.map((_, index) =>
      index === currentPriceIndex ? 5 : 0
    );

    return {
      chartData: {
        labels,
        datasets: [{
          label: isCommodity ? getDisplayName() : area,
          data,
          borderColor: baseColor,
          backgroundColor: baseColor.replace(/rgba?\(([^)]+)\)/, (_, p) => {
            const parts = p.split(',').map((x: string) => x.trim());
            return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, 0.06)`;
          }),
          borderWidth: 2,
          pointBackgroundColor: pointBackgroundColors,
          pointBorderColor: pointBorderColors,
          pointBorderWidth: 2,
          pointRadius,
          pointHoverRadius: 5,
          tension: 0.35,
          fill: true,
        }]
      },
      nowIndex: currentPriceIndex,
      nowColor: highlightColor,
      data: data,
    };
  };

  const createChartOptions = (nowIndex: number, nowColor: string, isCommodity: boolean = false, data: number[] = [], dataLength: number = 0) => {
    // For commodities, calculate focused y-axis range
    let yAxisConfig: any = {
      ticks: {
        font: {
          size: 10,
        },
      },
    };

    if (data.length > 0) {
      const values = data.filter(v => v !== null && v !== undefined);
      if (values.length > 0) {
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const range = maxValue - minValue;
        const padding = range * 0.15; // 15% padding above and below

        yAxisConfig.min = Math.max(0, minValue - padding);
        yAxisConfig.max = maxValue + padding;
      }
    } else {
      yAxisConfig.beginAtZero = true;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(3)} ${isCommodity ? 'USD' : unit}`;
            }
          }
        },
        // Pass per-chart options to the nowLine plugin
        nowLine: {
          index: nowIndex,
          color: nowColor,
        },
      },
      scales: {
        x: {
          ticks: {
            maxTicksLimit: viewMode === 'month' ? 10 : viewMode === 'week' ? 14 : 6,
            font: {
              size: 10,
            },
          },
        },
        y: yAxisConfig,
      },
    } as any;
  };

  const getTitleKey = () => {
    if (commodity === 'copper') return 'current.title.copper';
    if (commodity === 'crude') return 'current.title.crude';
    if (commodity === 'tesla') return 'current.title.tesla';
    if (commodity === 'nvidia') return 'current.title.nvidia';
    if (commodity === 'shell') return 'current.title.shell';
    if (commodity === 'nextera') return 'current.title.nextera';
    if (commodity === 'enphase') return 'current.title.enphase';
    return 'current.title';
  };

  return (
    <Card className="shadow-card">
      <Card.Body>
        <div className="cp-head">
          <h3 className="cp-title">{t(getTitleKey())}</h3>
          {onExpand && (
            <button
              type="button"
              className="cp-expand"
              onClick={onExpand}
              aria-label={t('current.expand') as string}
              title={t('current.expand') as string}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          )}
          <div className="cp-range" role="tablist" aria-label="Tidsperiod">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'today'}
              className={`cp-range-pill ${viewMode === 'today' ? 'active' : ''}`}
              onClick={() => setViewMode('today')}
            >
              {commodity === 'electricity' ? t('current.viewTodayTomorrow') : t('current.viewToday')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'week'}
              className={`cp-range-pill ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              {t('current.viewWeek')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'month'}
              className={`cp-range-pill ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              {t('current.viewMonth')}
            </button>
          </div>
        </div>
        {loadingPrices ? (
          <div className="d-flex justify-content-center my-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('current.loading')}</span>
            </div>
          </div>
        ) : (
          <Row>
            {(commodity === 'copper' || commodity === 'crude' || commodity === 'tesla' || commodity === 'nvidia' || commodity === 'shell' || commodity === 'nextera' || commodity === 'enphase') ? (
              // For commodities and stocks, show only the default price
              (() => {
                const currentPrice = getCurrentPrice(filteredPrices['default'] || []);
                const result = createAreaChartData(filteredPrices['default'] || [], 'default', true);
                const commodityColor = getCommodityColor(commodity!);
                
                // Get display name (localized for commodities)
                const displayName = getDisplayName();

                return (
                  <Col md={12} className="mb-4">
                    <div
                      className="current-price__summary"
                      style={{ ['--accent-color' as string]: commodityColor } as React.CSSProperties}
                    >
                      <span className="current-price__label">{displayName}</span>
                      <span>
                        <span className="current-price__value">{currentPrice ? Number(currentPrice.value).toFixed(2) : "-"}</span>
                        <span className="current-price__unit"> USD</span>
                      </span>
                    </div>
                    {result && (
                      <div className="current-price__chart">
                        <Line options={createChartOptions(result.nowIndex, result.nowColor, true, result.data)} data={result.chartData as any} />
                      </div>
                    )}
                  </Col>
                );
              })()
            ) : (
              // For electricity, show all SE areas — or just the one selected at
              // the top of the page.
              (selectedArea && selectedArea !== 'ALL' ? [selectedArea] : AREA_CODES).map((area) => {
                const currentPrice = getCurrentPrice(filteredPrices[area] || []);
                const result = createAreaChartData(filteredPrices[area] || [], area);
                return (
                  <Col key={area} md={12} className="mb-4">
                    <div
                      className="current-price__summary"
                      style={{ ['--accent-color' as string]: areaColor(area, darkMode) } as React.CSSProperties}
                    >
                      <span className="current-price__label">{area}</span>
                      <span>
                        <span className="current-price__value">{currentPrice?.value ?? "-"}</span>
                        <span className="current-price__unit"> {unit}</span>
                      </span>
                    </div>
                    {result && (
                      <div className="current-price__chart">
                        <Line options={createChartOptions(result.nowIndex, result.nowColor, false, result.data)} data={result.chartData as any} />
                      </div>
                    )}
                  </Col>
                );
              })
            )}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}

export { CurrentPrice };
